import {useColorScheme} from 'react-native';
import {create} from 'zustand';
import AsyncStorage from '@react-native-async-storage/async-storage';

/**
 * Light, dark, or whatever the device says.
 *
 * Until now the palette was `useColorScheme()` and nothing else: the app was
 * dark because the phone was, and there was no way to disagree. That is a
 * defensible default and a poor whole story — a warehouse terminal lives under
 * lights that make the system's idea of "evening" wrong, and half the people
 * who install a PWA expect the app to hold its own preference the way every
 * other app on their home screen does.
 *
 * So: three modes, one of which is `system` and is still the default. The
 * choice is stored under the same AsyncStorage the language uses (which the web
 * build shims onto `localStorage`), so it survives a reload and a cold start on
 * both platforms.
 *
 * Nothing here reads the palette. `useTheme()` in `./theme` is still the only
 * place a colour comes from; this module only decides which of the two it hands
 * back. `applyDocumentTheme` is a no-op on native and, on the web, keeps the
 * browser's own furniture — URL bar, task-switcher card, scrollbars, form
 * controls — in step with the choice.
 */
export type ThemeMode = 'system' | 'light' | 'dark';

export const THEME_MODES: readonly ThemeMode[] = ['system', 'light', 'dark'];

const STORAGE_KEY = 'samvision.theme-mode';

function isMode(value: unknown): value is ThemeMode {
  return value === 'system' || value === 'light' || value === 'dark';
}

type ThemeModeState = {
  mode: ThemeMode;
  /** False until the stored choice has been read, so the first paint can wait. */
  hydrated: boolean;
  setMode: (mode: ThemeMode) => void;
  hydrate: () => Promise<void>;
};

export const useThemeModeStore = create<ThemeModeState>()((set, get) => ({
  mode: 'system',
  hydrated: false,

  setMode: mode => {
    if (get().mode === mode) {
      return;
    }
    set({mode});
    void AsyncStorage.setItem(STORAGE_KEY, mode).catch(() => {
      // A failed write costs the choice on the NEXT launch, not this one.
      // Refusing to switch because storage is full would cost it immediately.
    });
  },

  hydrate: async () => {
    try {
      const stored = await AsyncStorage.getItem(STORAGE_KEY);
      if (isMode(stored)) {
        set({mode: stored});
      }
    } catch {
      // Unreadable storage means the device scheme stands. Nothing to report.
    } finally {
      set({hydrated: true});
    }
  },
}));

/** The chosen mode. `system` here means "ask the device", not "light". */
export function useThemeMode(): ThemeMode {
  return useThemeModeStore(s => s.mode);
}

/**
 * The scheme actually in force — the one question every palette lookup asks.
 *
 * Kept in this module rather than in `./theme` so that `theme.ts` stays a pure
 * table of colours with no store behind it, and so a test can drive the mode
 * without stubbing the palette.
 */
export function useResolvedScheme(): 'light' | 'dark' {
  const mode = useThemeMode();
  const system = useColorScheme();
  if (mode === 'system') {
    // `useColorScheme()` returns null when the platform cannot tell us — treat
    // that as dark: у рабочей станции тёмная тема по умолчанию (ui-kit), и пульт в
    // полумраке — обычное место кабинета.
    return system === 'light' ? 'light' : 'dark';
  }
  return mode;
}
